import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert, Avatar, Box, Button, Card, Chip, CircularProgress, Collapse, Divider,
  Grid, IconButton, InputAdornment, MenuItem, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TablePagination, TableRow, TextField, Tooltip,
  Typography, alpha, useMediaQuery, useTheme,
} from '@mui/material';
import {
  BadgeIndianRupee, ChevronDown, ChevronRight, Download, Info, Mail, Phone,
  RefreshCw, RotateCcw, Search, Trash2, Users as UsersIcon, Wallet, UserCheck
} from 'lucide-react';
import axios from '../utils/axios';
import { downloadCsv } from '../utils/downloadCsv';
import { formatDate, formatDateTime } from '../utils/formatDate';
import PageHeader from '../components/common/PageHeader';
import ConfirmDialog from '../components/common/ConfirmDialog';
import EmptyState from '../components/common/EmptyState';
import LoadingState from '../components/common/LoadingState';

const inr = (n) => `₹${Number(n || 0).toLocaleString('en-IN')}`;

// Compact ₹ for the KPI tiles, so ₹1,24,500 doesn't wrap the card.
const inrShort = (n) => {
  const v = Number(n || 0);
  if (Math.abs(v) >= 10000000) return `₹${(v / 10000000).toFixed(2)}Cr`;
  if (Math.abs(v) >= 100000) return `₹${(v / 100000).toFixed(2)}L`;
  if (Math.abs(v) >= 1000) return `₹${(v / 1000).toFixed(1)}K`;
  return `₹${v}`;
};

const fullName = (u) =>
  `${u?.first_name || ''} ${u?.last_name || ''}`.trim() || 'Deleted user';

const initials = (name) => {
  const parts = String(name || '').trim().split(/\s+/).filter(Boolean);
  if (!parts.length) return '?';
  return (parts.length > 1 ? `${parts[0][0]}${parts[1][0]}` : parts[0][0]).toUpperCase();
};

const STATUS_STYLE = {
  active: { label: 'Active', bg: 'rgba(34,197,94,0.14)', fg: '#15803d' },
  expired: { label: 'Expired', bg: 'rgba(244,67,54,0.12)', fg: '#c62828' },
  replaced: { label: 'Replaced', bg: 'rgba(145,158,171,0.16)', fg: '#637381' },
};

const METHOD_STYLE = {
  razorpay: { label: 'Razorpay', bg: 'rgba(33,150,243,0.12)', fg: '#1565c0' },
  wallet: { label: 'Wallet', bg: 'rgba(168,85,247,0.14)', fg: '#7e22ce' },
  free: { label: 'Free', bg: 'rgba(145,158,171,0.16)', fg: '#637381' },
};

const SORT_OPTIONS = [
  { value: 'recent', label: 'Newest first' },
  { value: 'oldest', label: 'Oldest first' },
  { value: 'amount_high', label: 'Highest paid' },
  { value: 'amount_low', label: 'Lowest paid' },
  { value: 'expiring', label: 'Expiring soonest' },
];

const EMPTY_FILTERS = {
  status: '',
  payment_method: '',
  billing_cycle: '',
  plan_id: '',
  paid: '',
  sort: 'recent',
};

/** Honest KPI tile — a number and its label, no decorative trend line. */
const Tile = ({ label, value, sub, icon, color }) => {
  const theme = useTheme();
  return (
    <Card sx={{ p: { xs: 1.75, md: 2.25 }, borderRadius: 3, height: '100%', width: '100%' }}>
      <Stack direction="row" spacing={1.5} alignItems="flex-start">
        <Box
          sx={{
            width: 38, height: 38, borderRadius: 2, flexShrink: 0,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            bgcolor: alpha(color, 0.12), color,
          }}
        >
          {icon}
        </Box>
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 700, color: 'text.secondary' }} noWrap>
            {label}
          </Typography>
          <Typography sx={{ fontSize: { xs: 20, md: 24 }, fontWeight: 800, lineHeight: 1.2, color: theme.palette.text.primary }}>
            {value}
          </Typography>
          {sub ? (
            <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontWeight: 600 }} noWrap>
              {sub}
            </Typography>
          ) : null}
        </Box>
      </Stack>
    </Card>
  );
};

const isLifetimeOrder = (o) =>
  Boolean(o?.is_lifetime_free) ||
  o?.billing_cycle === 'lifetime' ||
  Number(o?.plan_price) === 0 ||
  Number(o?.plan_price) === 29 ||
  Number(o?.effective_amount) === 0 ||
  Number(o?.effective_amount) === 29 ||
  Number(o?.amount_paid) === 0 ||
  Number(o?.amount_paid) === 29 ||
  Number(o?.plan_duration) === 0 ||
  (o?.plan_name || '').toLowerCase().includes('free');

const formatBillingCycle = (o) => {
  if (isLifetimeOrder(o)) return 'Lifetime Free';
  return o?.billing_cycle === 'yearly' ? 'Yearly' : 'Monthly';
};

/** Expanded row: everything about one purchase that doesn't fit a column. */
const OrderDetail = ({ order, history, historyLoading }) => {
  const rows = [
    ['Purchased', formatDateTime(order.createdAt || order.created_at)],
    ['Expires', isLifetimeOrder(order) ? 'Lifetime Free (No Expiry)' : (order.expiresAt || order.expires_at ? formatDateTime(order.expiresAt || order.expires_at) : 'No expiry recorded')],
    ['Billing cycle', formatBillingCycle(order)],
    ['Plan list price today', inr(order.list_price)],
    ['Wallet debit', order.wallet_debit ? inr(order.wallet_debit) : '—'],
    ['Razorpay payment ID', order.razorpay_payment_id || '—'],
    ['Razorpay order ID', order.razorpay_order_id || '—'],
    ['Lead quota', order.lead_quota_unlimited ? 'Unlimited' : `${order.lead_quota ?? 0}`],
    ['Leads used', order.lead_quota_unlimited ? '—' : `${order.leads_consumed ?? 0}`],
    ['Leads remaining', order.lead_quota_unlimited ? 'Unlimited' : `${order.leads_remaining ?? 0}`],
    ['Leads carried over', `${order.leads_carried_over ?? 0}`],
    ['Featured product slots', `${order.featureProduct_qty ?? 0}`],
  ];

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, bgcolor: 'background.neutral' }}>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 7 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', letterSpacing: 1, mb: 1.5 }}>
            THIS PURCHASE
          </Typography>
          <Grid container spacing={1}>
            {rows.map(([k, v]) => (
              <Grid size={{ xs: 12, sm: 6 }} key={k}>
                <Typography sx={{ fontSize: 11.5, color: 'text.disabled', fontWeight: 600 }}>{k}</Typography>
                <Typography sx={{ fontSize: 13.5, fontWeight: 600, wordBreak: 'break-all' }}>{v}</Typography>
              </Grid>
            ))}
          </Grid>
        </Grid>

        <Grid size={{ xs: 12, md: 5 }}>
          <Typography sx={{ fontSize: 12, fontWeight: 800, color: 'text.secondary', letterSpacing: 1, mb: 1.5 }}>
            THIS SUBSCRIBER'S HISTORY
          </Typography>
          {historyLoading ? (
            <Box sx={{ display: 'flex', py: 3, justifyContent: 'center' }}><CircularProgress size={22} /></Box>
          ) : (
            <>
              <Typography sx={{ fontSize: 13, fontWeight: 700, mb: 1 }}>
                Lifetime value: {inr(history?.lifetimeValue)}
                <Typography component="span" sx={{ fontSize: 12, color: 'text.disabled', fontWeight: 600, ml: 0.75 }}>
                  ({history?.orders?.length || 0} purchase{history?.orders?.length === 1 ? '' : 's'})
                </Typography>
              </Typography>
              <Stack spacing={0.75}>
                {(history?.orders || []).map((h) => (
                  <Stack
                    key={h._id}
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      p: 1, borderRadius: 2, bgcolor: 'background.paper',
                      border: (t) => `1px solid ${t.palette.divider}`,
                    }}
                  >
                    <Box sx={{ minWidth: 0 }}>
                      <Typography sx={{ fontSize: 13, fontWeight: 700 }} noWrap>{h.plan_name}</Typography>
                      <Typography sx={{ fontSize: 11, color: isLifetimeOrder(h) ? 'success.main' : 'text.disabled', fontWeight: 600 }}>
                        {formatDate(h.createdAt || h.created_at)} · {formatBillingCycle(h)}
                      </Typography>
                    </Box>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography sx={{ fontSize: 13, fontWeight: 800 }}>{inr(h.effective_amount)}</Typography>
                      <Chip
                        size="small"
                        label={STATUS_STYLE[h.status]?.label || h.status}
                        sx={{
                          height: 20, fontSize: 10.5, fontWeight: 800,
                          bgcolor: STATUS_STYLE[h.status]?.bg, color: STATUS_STYLE[h.status]?.fg,
                        }}
                      />
                    </Stack>
                  </Stack>
                ))}
              </Stack>
            </>
          )}
        </Grid>
      </Grid>
    </Box>
  );
};

const SubscribersView = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [rows, setRows] = useState([]);
  const [plans, setPlans] = useState([]);
  const [summary, setSummary] = useState(null);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [search, setSearch] = useState('');
  const [debounced, setDebounced] = useState('');
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);

  const [expandedId, setExpandedId] = useState(null);
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Debounce the search box so typing doesn't fire a query per keystroke.
  useEffect(() => {
    const t = setTimeout(() => { setDebounced(search); setPage(0); }, 400);
    return () => clearTimeout(t);
  }, [search]);

  // Only send filters that are actually set, so the server's own validation
  // (which ignores unknown values) never sees an empty string as a filter.
  const activeParams = useMemo(() => {
    const p = { page: page + 1, limit: rowsPerPage, sort: filters.sort };
    if (debounced) p.search = debounced;
    if (filters.status) p.status = filters.status;
    if (filters.payment_method) p.payment_method = filters.payment_method;
    if (filters.billing_cycle) p.billing_cycle = filters.billing_cycle;
    if (filters.plan_id) p.plan_id = filters.plan_id;
    if (filters.paid) p.paid = filters.paid;
    return p;
  }, [page, rowsPerPage, debounced, filters]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const { data } = await axios.get('/admin/subscriptions/orders', { params: activeParams });
      setRows(data.orders || []);
      setPlans(data.plans || []);
      setSummary(data.summary || null);
      setTotal(data.totalOrders || 0);
    } catch (e) {
      setError(e?.response?.data?.msg || 'Could not load subscribers.');
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [activeParams]);

  useEffect(() => { load(); }, [load]);

  const setFilter = (key) => (e) => {
    setPage(0);
    setFilters((f) => ({ ...f, [key]: e.target.value }));
  };

  const resetFilters = () => {
    setSearch('');
    setFilters(EMPTY_FILTERS);
    setPage(0);
  };

  const toggleRow = async (order) => {
    if (expandedId === order._id) { setExpandedId(null); return; }
    setExpandedId(order._id);
    setHistory(null);
    if (!order.user?._id) return;
    setHistoryLoading(true);
    try {
      const { data } = await axios.get(`/admin/subscriptions/orders/user/${order.user._id}`);
      setHistory(data);
    } catch (e) {
      setHistory({ orders: [], lifetimeValue: 0 });
    } finally {
      setHistoryLoading(false);
    }
  };

  const handleExport = async () => {
    setExporting(true);
    // The export covers the whole filtered set, so send the filters but not the
    // pagination.
    const { page: _p, limit: _l, ...exportParams } = activeParams;
    const res = await downloadCsv('/admin/subscriptions/orders/export', exportParams, 'subscribers.csv');
    if (!res.ok) setError(res.error || 'Could not export CSV.');
    setExporting(false);
  };

  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      setDeleting(true);
      await axios.delete(`/admin/subscriptions/orders/${deleteTarget._id}`);
      if (expandedId === deleteTarget._id) setExpandedId(null);
      setDeleteTarget(null);
      load();
    } catch (e) {
      setError(e?.response?.data?.msg || 'Could not delete the order.');
    } finally {
      setDeleting(false);
    }
  };

  const filtersDirty =
    !!debounced || Object.keys(EMPTY_FILTERS).some((k) => filters[k] !== EMPTY_FILTERS[k]);

  const tiles = summary ? [
    {
      label: 'Active subscribers', value: Number(summary.activeSubscribers || 0).toLocaleString('en-IN'),
      sub: `${Number(summary.uniqueSubscribers || 0).toLocaleString('en-IN')} ever subscribed`,
      icon: <UsersIcon size={19} />, color: theme.palette.success.main,
    },
    {
      label: 'Revenue in view', value: inrShort(summary.totalRevenue),
      sub: `${Number(summary.paidOrders || 0).toLocaleString('en-IN')} paid orders`,
      icon: <BadgeIndianRupee size={19} />, color: theme.palette.primary.main,
    },
    {
      label: 'Razorpay', value: inrShort(summary.razorpayRevenue),
      sub: 'card / UPI / netbanking',
      icon: <BadgeIndianRupee size={19} />, color: '#1565c0',
    },
    {
      label: 'Referral wallet', value: inrShort(summary.walletRevenue),
      sub: 'paid from wallet balance',
      icon: <Wallet size={19} />, color: '#7e22ce',
    },
    {
      label: 'Active plans', value: Number(summary.activeOrders || 0).toLocaleString('en-IN'),
      sub: `${Number(summary.expiredOrders || 0).toLocaleString('en-IN')} expired`,
      icon: <UsersIcon size={19} />, color: theme.palette.info.main,
    },
    {
      label: 'Free activations', value: Number(summary.freeOrders || 0).toLocaleString('en-IN'),
      sub: 'no money collected',
      icon: <UsersIcon size={19} />, color: theme.palette.grey[600],
    },
  ] : [];

  const columns = ['', 'Subscriber', 'Plan', 'Amount paid', 'Method', 'Status', 'Purchased', 'Expires', 'Leads', ''];

  return (
    <Box sx={{ py: 1 }}>
      <PageHeader
        title="Subscribers"
        subtitle="Every plan purchase — who bought it, what they paid, and when it runs out."
        count={total}
        countLabel="orders"
        breadcrumbs={[
          { label: 'Dashboard', path: '/dashboard' },
          { label: 'Commerce', path: '/users' },
          { label: 'Subscribers' },
        ]}
        action={
          <Button
            variant="outlined"
            size="small"
            startIcon={<Download size={16} />}
            onClick={handleExport}
            disabled={exporting || loading}
          >
            {exporting ? 'Exporting…' : 'Export CSV'}
          </Button>
        }
        secondaryAction={
          <Tooltip title="Refresh">
            <IconButton onClick={load} disabled={loading} size="small" aria-label="Refresh orders">
              <RefreshCw size={18} />
            </IconButton>
          </Tooltip>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2.5 }} onClose={() => setError('')}>{error}</Alert>}

      {/* KPIs — these describe the CURRENT filter, not the whole database, so an
          admin reading "Revenue in view" after filtering by plan gets that
          plan's revenue rather than a number that silently ignores the filter. */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {tiles.map((t) => (
          <Grid size={{ xs: 6, sm: 4, md: 2 }} key={t.label} sx={{ display: 'flex' }}>
            <Tile {...t} />
          </Grid>
        ))}
      </Grid>

      {summary?.derivedAmountOrders > 0 && (
        <Alert severity="info" icon={<Info size={18} />} sx={{ mb: 2.5 }}>
          {summary.derivedAmountOrders} of these orders predate amount tracking, so their
          amount is reconstructed from the plan's <strong>current</strong> price and moves if
          that price is edited. They are marked <em>est.</em> in the table. Orders placed from
          now on record the exact amount charged.
        </Alert>
      )}

      {/* Filters */}
      <Card sx={{ p: 2, borderRadius: 3, mb: 2 }}>
        <Grid container spacing={1.5} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              fullWidth size="small" placeholder="Name, email, phone, plan or payment ID"
              value={search} onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <InputAdornment position="start"><Search size={16} /></InputAdornment> }}
            />
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
            <TextField fullWidth size="small" select label="Status" value={filters.status} onChange={setFilter('status')}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>
              <MenuItem value="replaced">Replaced</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
            <TextField fullWidth size="small" select label="Plan" value={filters.plan_id} onChange={setFilter('plan_id')}>
              <MenuItem value="">All plans</MenuItem>
              {plans.map((p) => (
                <MenuItem key={p._id} value={p._id}>{p.plan_name}</MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
            <TextField fullWidth size="small" select label="Method" value={filters.payment_method} onChange={setFilter('payment_method')}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="razorpay">Razorpay</MenuItem>
              <MenuItem value="wallet">Wallet</MenuItem>
              <MenuItem value="free">Free</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
            <TextField fullWidth size="small" select label="Cycle" value={filters.billing_cycle} onChange={setFilter('billing_cycle')}>
              <MenuItem value="">All</MenuItem>
              <MenuItem value="lifetime">Lifetime Free</MenuItem>
              <MenuItem value="monthly">Monthly</MenuItem>
              <MenuItem value="yearly">Yearly</MenuItem>
            </TextField>
          </Grid>
          <Grid size={{ xs: 6, sm: 4, md: 1.6 }}>
            <TextField fullWidth size="small" select label="Sort" value={filters.sort} onChange={setFilter('sort')}>
              {SORT_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
            </TextField>
          </Grid>
          {filtersDirty && (
            <Grid size={12}>
              <Button
                size="small" startIcon={<RotateCcw size={15} />} onClick={resetFilters}
                sx={{ textTransform: 'none', fontWeight: 700 }}
              >
                Reset filters
              </Button>
            </Grid>
          )}
        </Grid>
      </Card>

      {/* Table */}
      <Card sx={{ borderRadius: 2.5, overflow: 'hidden' }}>
        <TableContainer sx={{ overflowX: 'auto' }}>
          <Table size={isMobile ? 'small' : 'medium'}>
            <TableHead>
              <TableRow>
                {columns.map((h, i) => (
                  <TableCell key={`${h}-${i}`} sx={{ whiteSpace: 'nowrap' }}>
                    {h}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <LoadingState rows={rowsPerPage} cols={columns.length} />
              ) : rows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={columns.length} sx={{ p: 0, border: 'none' }}>
                    <EmptyState
                      icon={UserCheck}
                      title="No subscribers found"
                      description={filtersDirty ? 'No subscribers match these filters.' : 'No plan purchases yet.'}
                      actionLabel={filtersDirty ? 'Reset Filters' : undefined}
                      onAction={filtersDirty ? resetFilters : undefined}
                    />
                  </TableCell>
                </TableRow>
              ) : null}
              {!loading && rows.map((o) => {
                    const open = expandedId === o._id;
                    const name = fullName(o.user);
                    const st = STATUS_STYLE[o.status] || STATUS_STYLE.replaced;
                    const mt = METHOD_STYLE[o.resolved_method] || METHOD_STYLE.free;
                    return (
                      <React.Fragment key={o._id}>
                        <TableRow hover sx={{ '& > td': { borderBottom: open ? 'none' : undefined } }}>
                          <TableCell sx={{ width: 44, pr: 0 }}>
                            <IconButton size="small" onClick={() => toggleRow(o)} aria-label={open ? 'Hide details' : 'Show details'}>
                              {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
                            </IconButton>
                          </TableCell>

                          <TableCell sx={{ minWidth: 220 }}>
                            <Stack direction="row" spacing={1.25} alignItems="center">
                              <Avatar sx={{ width: 34, height: 34, fontSize: 13, fontWeight: 800, bgcolor: alpha(theme.palette.primary.main, 0.12), color: 'primary.main' }}>
                                {initials(name)}
                              </Avatar>
                              <Box sx={{ minWidth: 0 }}>
                                <Typography sx={{ fontWeight: 700, fontSize: 14 }} noWrap>{name}</Typography>
                                <Stack direction="row" spacing={1} alignItems="center" sx={{ color: 'text.disabled' }}>
                                  {o.user?.email && (
                                    <Tooltip title={o.user.email}>
                                      <Stack direction="row" spacing={0.4} alignItems="center" sx={{ maxWidth: 170 }}>
                                        <Mail size={11} />
                                        <Typography sx={{ fontSize: 11 }} noWrap>{o.user.email}</Typography>
                                      </Stack>
                                    </Tooltip>
                                  )}
                                  {o.user?.phone && (
                                    <Stack direction="row" spacing={0.4} alignItems="center">
                                      <Phone size={11} />
                                      <Typography sx={{ fontSize: 11 }}>{o.user.phone}</Typography>
                                    </Stack>
                                  )}
                                </Stack>
                              </Box>
                            </Stack>
                          </TableCell>

                          <TableCell sx={{ minWidth: 150 }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13.5 }}>{o.plan_name}</Typography>
                            <Typography sx={{ fontSize: 11, color: isLifetimeOrder(o) ? 'success.main' : 'text.disabled', fontWeight: 700 }}>
                              {formatBillingCycle(o)}
                              {o.user?.userType ? ` · ${o.user.userType}` : ''}
                            </Typography>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap' }}>
                            <Typography sx={{ fontWeight: 800, fontSize: 14 }}>{inr(o.effective_amount)}</Typography>
                            {o.amount_is_derived && (
                              <Tooltip title="Reconstructed from the plan's current price — this order predates amount tracking.">
                                <Typography sx={{ fontSize: 10.5, color: 'warning.dark', fontWeight: 800 }}>est.</Typography>
                              </Tooltip>
                            )}
                          </TableCell>

                          <TableCell>
                            <Chip size="small" label={mt.label} sx={{ fontWeight: 800, fontSize: 11, bgcolor: mt.bg, color: mt.fg }} />
                          </TableCell>

                          <TableCell>
                            <Chip size="small" label={st.label} sx={{ fontWeight: 800, fontSize: 11, bgcolor: st.bg, color: st.fg }} />
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>
                            {formatDate(o.createdAt || o.created_at)}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>
                            {isLifetimeOrder(o) ? (
                              <Chip
                                size="small"
                                label="Lifetime Free"
                                sx={{
                                  fontWeight: 800,
                                  fontSize: 10.5,
                                  bgcolor: 'rgba(34,197,94,0.14)',
                                  color: '#15803d',
                                  height: 22,
                                }}
                              />
                            ) : (o.expiresAt || o.expires_at ? (
                              <>
                                {formatDate(o.expiresAt || o.expires_at)}
                                {o.status === 'active' && o.days_left !== null && o.days_left !== undefined && (
                                  <Typography
                                    sx={{
                                      fontSize: 10.5, fontWeight: 800,
                                      color: o.days_left <= 7 ? 'error.main' : 'text.disabled',
                                    }}
                                  >
                                    {o.days_left} day{o.days_left === 1 ? '' : 's'} left
                                  </Typography>
                                )}
                              </>
                            ) : '—')}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 12.5 }}>
                            {o.lead_quota_unlimited
                              ? 'Unlimited'
                              : `${o.leads_consumed ?? 0} / ${(Number(o.leads_consumed) || 0) + (Number(o.leads_remaining) || 0)}`}
                          </TableCell>

                          <TableCell align="right">
                            <Tooltip title="Delete this order record">
                              <IconButton size="small" color="error" onClick={() => setDeleteTarget(o)}>
                                <Trash2 size={15} />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </TableRow>

                        <TableRow>
                          <TableCell colSpan={columns.length} sx={{ py: 0, borderBottom: open ? undefined : 'none' }}>
                            <Collapse in={open} timeout="auto" unmountOnExit>
                              <OrderDetail order={o} history={history} historyLoading={historyLoading} />
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      </React.Fragment>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>

            <Divider />
            <TablePagination
              component="div"
              count={total}
              page={page}
              onPageChange={(_, p) => { setPage(p); setExpandedId(null); }}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => { setRowsPerPage(parseInt(e.target.value, 10)); setPage(0); }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              labelRowsPerPage={isMobile ? '' : 'Rows per page:'}
              sx={{
                border: 'none',
                '& .MuiTablePagination-selectLabel, & .MuiTablePagination-input': {
                  display: isMobile ? 'none' : 'block',
                },
              }}
            />
          </Card>

          <ConfirmDialog
            open={Boolean(deleteTarget)}
            title="Delete Order Record"
            content={
              deleteTarget
                ? `Delete ${fullName(deleteTarget.user)}'s "${deleteTarget.plan_name}" order (${inr(deleteTarget.effective_amount)})? This permanently removes the purchase record and its revenue from every report. It does NOT refund the payment and does NOT change the seller's current entitlements.`
                : ''
            }
            confirmLabel="Delete Order"
            onConfirm={confirmDelete}
            onClose={() => setDeleteTarget(null)}
            loading={deleting}
          />
        </Box>
  );
};

export default SubscribersView;
